import Psd from "@webtoon/psd";
import { decode as decodePng, encode as encodePng } from "fast-png";
import { customShader, glFilters, saturate } from "webgl-filters";
import SpecsRevisionService, {
  type SpecType,
} from "../../service/SpecsRevisionService";
import type { OutputImageRole, SpecsLayerModelPath } from "./types";
import { resolveSpecsImageUrl } from "./specsAssetUrls";

export type SpecsImageUploadContext = {
  specType?: SpecType;
  specName?: string;
  fieldPath?: string;
  specsServiceUrl?: string;
};

export type SpecsLayerImageUploadIdentity = SpecsLayerModelPath & {
  layerName: string;
  role: OutputImageRole;
  side: string;
};

export type SpecsImageUploadValidationOptions = {
  allowedContentTypes?: string[];
  normalizeOversizedRaster?: boolean;
};

export type SpecsGrayscaleImageProcessingResult = {
  file: File | null;
  wasAlreadySingleChannel: boolean;
};
export type SpecsRGBAImageProcessingResult = {
  file: File | null;
  wasAlreadyRGBA: boolean;
};
export type SpecsImageDimensions = {
  width: number;
  height: number;
};

type GlImageData = {
  data: Uint8ClampedArray | ArrayLike<number>;
  width: number;
  height: number;
};

type SpecsImageProcessingInput = {
  sourceUrl: string;
  isSingleChannelPng: boolean;
};

export const DEFAULT_SPECS_IMAGE_UPLOAD_ACCEPT =
  "image/png,image/jpeg,image/webp,image/svg+xml,.psd";
export const DEFAULT_SPECS_IMAGE_UPLOAD_VALIDATION: Required<SpecsImageUploadValidationOptions> =
  {
    allowedContentTypes: [
      "image/png",
      "image/jpeg",
      "image/webp",
      "image/svg+xml",
    ],
    normalizeOversizedRaster: true,
  };

export const SPECS_UPLOAD_TARGET_MAX_WIDTH = 700;
export const SPECS_UPLOAD_TARGET_MAX_HEIGHT = 1000;
export const SPECS_UPLOAD_MAX_PIXEL_AREA =
  SPECS_UPLOAD_TARGET_MAX_WIDTH * SPECS_UPLOAD_TARGET_MAX_HEIGHT;

export function readSpecsImageBlobAsDataUrl(blob: Blob, label = "image") {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === "string") {
        resolve(reader.result);
        return;
      }

      reject(new Error(`Failed to read "${label}"`));
    };
    reader.onerror = () => reject(new Error(`Failed to read "${label}"`));
    reader.readAsDataURL(blob);
  });
}

function readSpecsImageBlobAsArrayBuffer(blob: Blob, label = "image") {
  return new Promise<ArrayBuffer>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (reader.result instanceof ArrayBuffer) {
        resolve(reader.result);
        return;
      }

      reject(new Error(`Failed to read "${label}"`));
    };
    reader.onerror = () => reject(new Error(`Failed to read "${label}"`));
    reader.readAsArrayBuffer(blob);
  });
}

export async function resolveFalImageInputUrl(url: string) {
  if (url.startsWith("data:")) {
    return url;
  }

  const resolvedUrl = resolveSpecsImageUrl(url);
  const response = await fetch(resolvedUrl);
  if (!response.ok) {
    throw new Error(
      `Failed to read image "${resolvedUrl}": ${response.status} ${response.statusText}`
    );
  }

  return readSpecsImageBlobAsDataUrl(await response.blob(), resolvedUrl);
}

export function resolveFalImageInputUrls(urls: string[]) {
  return Promise.all(urls.map(resolveFalImageInputUrl));
}

export function shouldNormalizeImportedImageFile(file: File): boolean {
  return /\.(png|jpe?g|webp)$/i.test(file.name);
}

function isPsdImageFile(file: File) {
  return /\.psd$/i.test(file.name);
}

function inferContentType(file: File) {
  if (file.type) {
    return file.type;
  }

  const lowerName = file.name.toLowerCase();
  if (lowerName.endsWith(".png")) return "image/png";
  if (lowerName.endsWith(".jpg") || lowerName.endsWith(".jpeg")) {
    return "image/jpeg";
  }
  if (lowerName.endsWith(".webp")) return "image/webp";
  if (lowerName.endsWith(".svg")) return "image/svg+xml";

  return "";
}

export function getSpecsImageExtension(contentType: string) {
  if (contentType === "image/jpeg") return "jpg";
  if (contentType === "image/webp") return "webp";
  if (contentType === "image/svg+xml") return "svg";
  return "png";
}

export function slugFileSegment(value: string) {
  return value
    .trim()
    .replace(/([a-z0-9])([A-Z])/g, "$1-$2")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export function buildSpecsLayerImageFileNameBase(
  identity: SpecsLayerImageUploadIdentity
) {
  return [
    identity.partName,
    identity.elementName,
    identity.styleName,
    identity.layerName,
    identity.side,
  ]
    .map(slugFileSegment)
    .filter(Boolean)
    .join("-");
}

export function buildSpecsLayerImageFileName(
  identity: SpecsLayerImageUploadIdentity,
  contentType: string
) {
  return `${buildSpecsLayerImageFileNameBase(identity)}.${getSpecsImageExtension(contentType)}`;
}

export function buildSpecsLayerImageFieldPath(
  identity: SpecsLayerImageUploadIdentity
) {
  return [
    "parts",
    identity.partName,
    "elements",
    identity.elementName,
    "styles",
    identity.styleName,
    "layers",
    identity.layerName,
    identity.role,
    identity.side,
  ].join(".");
}

function validateSpecsImageUploadFile(
  file: File,
  validation: SpecsImageUploadValidationOptions = {}
) {
  const contentType = inferContentType(file);
  const allowedContentTypes =
    validation.allowedContentTypes ??
    DEFAULT_SPECS_IMAGE_UPLOAD_VALIDATION.allowedContentTypes;

  if (!contentType || !allowedContentTypes.includes(contentType)) {
    throw new Error("Unsupported image type");
  }

  return contentType;
}

export function getSpecsUploadResizeDimensions(width: number, height: number) {
  if (width <= 0 || height <= 0) {
    throw new Error("Invalid image dimensions");
  }

  const pixelArea = width * height;
  if (pixelArea <= SPECS_UPLOAD_MAX_PIXEL_AREA) {
    return null;
  }

  const adjustmentRatio = Math.sqrt(SPECS_UPLOAD_MAX_PIXEL_AREA / pixelArea);

  return {
    width: Math.max(1, Math.floor(width * adjustmentRatio)),
    height: Math.max(1, Math.floor(height * adjustmentRatio)),
  };
}

function loadImageFromObjectUrl(objectUrl: string) {
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();

    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("Failed to read image"));
    image.src = objectUrl;
  });
}

export async function getSpecsImageDimensions(
  imageUrl: string
): Promise<SpecsImageDimensions> {
  const response = await fetch(resolveSpecsImageUrl(imageUrl));
  if (!response.ok) {
    throw new Error(`Failed to read image (${response.status})`);
  }

  const objectUrl = URL.createObjectURL(await response.blob());
  try {
    const image = await loadImageFromObjectUrl(objectUrl);
    return {
      width: image.naturalWidth || image.width,
      height: image.naturalHeight || image.height,
    };
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

function canvasToBlob(
  canvas: HTMLCanvasElement,
  contentType: string,
  quality?: number
) {
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
          return;
        }

        reject(new Error("Failed to process image"));
      },
      contentType,
      quality
    );
  });
}

async function glImageDataToGrayscalePngFile(
  result: GlImageData,
  fileName: string
) {
  const rgba = result.data;
  const gray = new Uint8Array(result.width * result.height);
  for (let sourceIndex = 0, targetIndex = 0; targetIndex < gray.length; ) {
    gray[targetIndex] = rgba[sourceIndex];
    sourceIndex += 4;
    targetIndex += 1;
  }

  const encoded = encodePng({
    width: result.width,
    height: result.height,
    data: gray,
    depth: 8,
    channels: 1,
  });

  return new File([encoded], fileName, { type: "image/png" });
}

async function glImageDataToRGBAPngFile(result: GlImageData, fileName: string) {
  const encoded = encodePng({
    width: result.width,
    height: result.height,
    data:
      result.data instanceof Uint8Array
        ? result.data
        : Uint8Array.from(result.data),
    depth: 8,
    channels: 4,
  });

  return new File([encoded], fileName, { type: "image/png" });
}

function hasColorPixels(result: GlImageData) {
  const data = result.data;
  for (let index = 0; index < data.length; index += 4) {
    if (data[index] > 0) {
      return true;
    }
  }

  return false;
}

async function isSingleChannelPngBlob(blob: Blob, label: string) {
  try {
    const decoded = decodePng(
      await readSpecsImageBlobAsArrayBuffer(blob, label)
    );
    return decoded.channels === 1;
  } catch {
    return false;
  }
}

async function resolveSpecsImageProcessingInput(
  imageUrl: string
): Promise<SpecsImageProcessingInput> {
  const resolvedUrl = resolveSpecsImageUrl(imageUrl);
  if (resolvedUrl.startsWith("data:") || resolvedUrl.startsWith("blob:")) {
    const response = await fetch(resolvedUrl);
    const blob = await response.blob();
    return {
      sourceUrl: resolvedUrl,
      isSingleChannelPng: await isSingleChannelPngBlob(blob, resolvedUrl),
    };
  }

  const response = await fetch(resolvedUrl);
  if (!response.ok) {
    throw new Error(`Failed to read image (${response.status})`);
  }

  const blob = await response.blob();
  return {
    sourceUrl: await readSpecsImageBlobAsDataUrl(blob, resolvedUrl),
    isSingleChannelPng: await isSingleChannelPngBlob(blob, resolvedUrl),
  };
}

async function createPsdCompositePngFile(file: File) {
  const psd = Psd.parse(await readSpecsImageBlobAsArrayBuffer(file, file.name));
  const pixels = await psd.composite();
  const canvas = document.createElement("canvas");
  canvas.width = psd.width;
  canvas.height = psd.height;

  const context = canvas.getContext("2d");
  if (!context) {
    throw new Error("Failed to process PSD image");
  }

  const imageData = context.createImageData(psd.width, psd.height);
  imageData.data.set(pixels);
  context.putImageData(imageData, 0, 0);

  const blob = await canvasToBlob(canvas, "image/png");
  const baseName = file.name.replace(/\.psd$/i, "") || "image";

  return new File([blob], `${baseName}.png`, {
    type: "image/png",
    lastModified: file.lastModified,
  });
}

export async function normalizeSpecsImageUploadFile(
  file: File,
  validation: SpecsImageUploadValidationOptions = {}
) {
  if (isPsdImageFile(file)) {
    return normalizeSpecsImageUploadFile(
      await createPsdCompositePngFile(file),
      validation
    );
  }

  if (!shouldNormalizeImportedImageFile(file)) {
    return {
      file,
      contentType: validateSpecsImageUploadFile(file, validation),
    };
  }

  const contentType = validateSpecsImageUploadFile(file, validation);
  const normalizeOversizedRaster =
    validation.normalizeOversizedRaster ??
    DEFAULT_SPECS_IMAGE_UPLOAD_VALIDATION.normalizeOversizedRaster;

  if (contentType === "image/svg+xml" || !normalizeOversizedRaster) {
    return {
      file,
      contentType,
    };
  }

  const objectUrl = URL.createObjectURL(file);

  try {
    const image = await loadImageFromObjectUrl(objectUrl);
    const resizedDimensions = getSpecsUploadResizeDimensions(
      image.naturalWidth || image.width,
      image.naturalHeight || image.height
    );

    if (!resizedDimensions) {
      return {
        file,
        contentType,
      };
    }

    const canvas = document.createElement("canvas");
    canvas.width = resizedDimensions.width;
    canvas.height = resizedDimensions.height;

    const context = canvas.getContext("2d");
    if (!context) {
      throw new Error("Failed to process image");
    }

    context.drawImage(image, 0, 0, canvas.width, canvas.height);

    const blob = await canvasToBlob(canvas, contentType);

    return {
      file: new File([blob], file.name, {
        type: contentType,
        lastModified: file.lastModified,
      }),
      contentType,
    };
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

export async function createSpecsGrayscaleImageFile(
  imageUrl: string,
  fileNameBase: string
): Promise<SpecsGrayscaleImageProcessingResult> {
  const { sourceUrl, isSingleChannelPng } =
    await resolveSpecsImageProcessingInput(imageUrl);
  if (isSingleChannelPng) {
    return {
      file: null,
      wasAlreadySingleChannel: true,
    };
  }

  const colorCheck = await glFilters()
    .addFilter(
      customShader({
        source: `
          vec4 color = texture2D(u_texture, v_texCoord);
          float delta = max(
            max(abs(color.r - color.g), abs(color.r - color.b)),
            abs(color.g - color.b)
          );
          float hasColor = step(0.001, delta);
          gl_FragColor = vec4(hasColor, hasColor, hasColor, 1.0);
        `,
      })
    )
    .applyAsync(sourceUrl);
  const hasColor = hasColorPixels(colorCheck);
  const grayscale = hasColor
    ? await glFilters()
        .addFilter(saturate({ factor: 0 }))
        .applyAsync(sourceUrl)
    : await glFilters().applyAsync(sourceUrl);

  return {
    file: await glImageDataToGrayscalePngFile(grayscale, `${fileNameBase}.png`),
    wasAlreadySingleChannel: false,
  };
}

export async function uploadSpecsGrayscaleImageUrl(
  imageUrl: string,
  fileNameBase: string,
  context: SpecsImageUploadContext = {}
) {
  const { file, wasAlreadySingleChannel } = await createSpecsGrayscaleImageFile(
    imageUrl,
    fileNameBase
  );

  if (wasAlreadySingleChannel) {
    return {
      assetUrl: imageUrl,
      uploaded: false,
    };
  }

  if (!file) {
    throw new Error("Failed to create grayscale image");
  }

  return {
    assetUrl: await uploadSpecsImageFile(file, context, {
      allowedContentTypes: ["image/png"],
      normalizeOversizedRaster: false,
    }),
    uploaded: true,
  };
}

export async function createSpecsRGBAImageFile(
  imageUrl: string,
  fileNameBase: string
): Promise<SpecsRGBAImageProcessingResult> {
  const { sourceUrl, isSingleChannelPng } =
    await resolveSpecsImageProcessingInput(imageUrl);

  if (!isSingleChannelPng) {
    return {
      file: null,
      wasAlreadyRGBA: true,
    };
  }

  const rgba = await glFilters()
    .addFilter(
      customShader({
        source: `
          vec4 color = texture2D(u_texture, v_texCoord);
          gl_FragColor = vec4(1.0, 1.0, 1.0, color.r);
        `,
      })
    )
    .applyAsync(sourceUrl);
  return {
    file: await glImageDataToRGBAPngFile(rgba, `${fileNameBase}.png`),
    wasAlreadyRGBA: false,
  };
}

export async function uploadSpecsRGBAImageUrl(
  imageUrl: string,
  fileNameBase: string,
  context: SpecsImageUploadContext = {}
) {
  const { file, wasAlreadyRGBA } = await createSpecsRGBAImageFile(
    imageUrl,
    fileNameBase
  );

  if (wasAlreadyRGBA) {
    return {
      assetUrl: imageUrl,
      uploaded: false,
    };
  }

  if (!file) {
    throw new Error("Failed to create RGBA image");
  }

  return {
    assetUrl: await uploadSpecsImageFile(file, context, {
      allowedContentTypes: ["image/png"],
      normalizeOversizedRaster: false,
    }),
    uploaded: true,
  };
}

export async function uploadSpecsImageFile(
  file: File,
  context: SpecsImageUploadContext = {},
  validation: SpecsImageUploadValidationOptions = {}
) {
  const normalized = await normalizeSpecsImageUploadFile(file, validation);
  const { file: uploadFile, contentType } = normalized;

  const response = await SpecsRevisionService.createImageUpload(
    {
      fileName: uploadFile.name,
      contentType,
      specType: context.specType,
      specName: context.specName,
      fieldPath: context.fieldPath,
    },
    {
      specsServiceUrl: context.specsServiceUrl,
    }
  );

  const formData = new FormData();
  for (const [key, value] of Object.entries(response.data.fields)) {
    formData.append(key, value);
  }
  formData.append("file", uploadFile);

  const uploadResponse = await fetch(response.data.uploadUrl, {
    method: "POST",
    body: formData,
  });

  if (!uploadResponse.ok) {
    throw new Error(`Upload failed (${uploadResponse.status})`);
  }

  return response.data.assetUrl;
}

export async function uploadSpecsImageUrl(
  imageUrl: string,
  fileNameBase: string,
  context: SpecsImageUploadContext = {},
  validation: SpecsImageUploadValidationOptions = {}
) {
  const response = await fetch(resolveSpecsImageUrl(imageUrl));
  if (!response.ok) {
    throw new Error(`Failed to read image (${response.status})`);
  }

  const blob = await response.blob();
  const contentType = blob.type || "image/png";
  const file = new File(
    [blob],
    `${fileNameBase}.${getSpecsImageExtension(contentType)}`,
    { type: contentType }
  );

  return uploadSpecsImageFile(file, context, validation);
}

export async function uploadSpecsImageUrlResizedToDimensions(
  imageUrl: string,
  fileNameBase: string,
  dimensions: SpecsImageDimensions,
  context: SpecsImageUploadContext = {}
) {
  const response = await fetch(resolveSpecsImageUrl(imageUrl));
  if (!response.ok) {
    throw new Error(`Failed to read image (${response.status})`);
  }

  const objectUrl = URL.createObjectURL(await response.blob());
  try {
    const image = await loadImageFromObjectUrl(objectUrl);
    const canvas = document.createElement("canvas");
    canvas.width = dimensions.width;
    canvas.height = dimensions.height;

    const canvasContext = canvas.getContext("2d");
    if (!canvasContext) {
      throw new Error("Failed to process image");
    }

    canvasContext.drawImage(image, 0, 0, dimensions.width, dimensions.height);
    const blob = await canvasToBlob(canvas, "image/png");
    const file = new File([blob], `${fileNameBase}.png`, {
      type: "image/png",
    });

    return uploadSpecsImageFile(file, context, {
      allowedContentTypes: ["image/png"],
      normalizeOversizedRaster: false,
    });
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}
